import { useCallback, useEffect, useRef, useState } from 'react';
import { TAutoReplyMessageAsset } from 'api';
import { MAX_BULK_MESSAGE_SECTIONS } from '../../constants';
import { autoReplyMessageStrings } from '../../constants/strings';
import ContentSection from '../SendMessageForAll/ContentSection';
import {
  EContentSectionType,
  EMoveDirection,
  TMessageSection,
  TSectionPatch,
} from 'types';
import { createSection } from 'utils/messageSection';

type Props = {
  sections: TMessageSection[];
  assets: TAutoReplyMessageAsset[];
  onChange: (sections: TMessageSection[]) => void;
  /** アンケート招待画像のアップロード進行中は親の保存を止めるための集約通知 */
  onUploadingChange?: (uploading: boolean) => void;
};

export function AutoReplySectionsEditor({
  sections,
  assets,
  onChange,
  onUploadingChange,
}: Props) {
  const [uploadingSectionIds, setUploadingSectionIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;
  const handleSurveyUploadingChange = useCallback(
    (sectionId: string, uploading: boolean) => {
      setUploadingSectionIds((current) => {
        if (current.has(sectionId) === uploading) return current;
        const next = new Set(current);
        if (uploading) {
          next.add(sectionId);
        } else {
          next.delete(sectionId);
        }
        return next;
      });
    },
    [],
  );
  useEffect(() => {
    onUploadingChangeRef.current?.(uploadingSectionIds.size > 0);
  }, [uploadingSectionIds]);
  const handleAddSection = useCallback(() => {
    onChange([...sections, createSection(EContentSectionType.TEXT)]);
  }, [onChange, sections]);

  const handlePatchSection = useCallback(
    (id: string, patch: TSectionPatch) => {
      onChange(
        sections.map((section) => {
          if (section.id !== id) return section;
          if (section.type === EContentSectionType.TEXT) {
            if (!('content' in patch)) return section;
            return { ...section, content: patch.content };
          }
          if (!('file' in patch)) return section;
          return { ...section, file: patch.file };
        }),
      );
    },
    [onChange, sections],
  );

  const handleChangeType = useCallback(
    (id: string, nextType: EContentSectionType) => {
      onChange(
        sections.map((section) =>
          section.id === id ? createSection(nextType, { assets }) : section,
        ),
      );
    },
    [assets, onChange, sections],
  );

  const handleDeleteSection = useCallback(
    (id: string) => {
      if (sections.length <= 1) return;
      onChange(sections.filter((section) => section.id !== id));
    },
    [onChange, sections],
  );

  const handleMoveSection = useCallback(
    (id: string, direction: EMoveDirection) => {
      const index = sections.findIndex((section) => section.id === id);
      if (index < 0) return;
      const next = [...sections];
      const targetIndex =
        direction === EMoveDirection.UP ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= next.length) return;
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      onChange(next);
    },
    [onChange, sections],
  );

  const handleUpdateSection = useCallback(
    (id: string, updates: Partial<TMessageSection>) => {
      onChange(
        sections.map((section) => {
          if (section.id !== id) return section;
          return { ...section, ...updates } as TMessageSection;
        }),
      );
    },
    [onChange, sections],
  );

  return (
    <div className="grid gap-4">
      {sections.map((section, index) => (
        <ContentSection
          key={section.id}
          section={section}
          index={index}
          totalSections={sections.length}
          variant="autoReply"
          assets={assets}
          hideActionUrl
          onChangeType={(type) => handleChangeType(section.id, type)}
          onUpdateSection={(updates) =>
            handleUpdateSection(section.id, updates)
          }
          onChangeContent={(content) =>
            handlePatchSection(section.id, { content })
          }
          onChangeFile={(file) => handlePatchSection(section.id, { file })}
          onRemoveFile={() => handlePatchSection(section.id, { file: null })}
          onDelete={() => handleDeleteSection(section.id)}
          onMove={(direction) => handleMoveSection(section.id, direction)}
          onSurveyUploadingChange={(uploading) =>
            handleSurveyUploadingChange(section.id, uploading)
          }
        />
      ))}

      {sections.length < MAX_BULK_MESSAGE_SECTIONS && (
        <button
          type="button"
          onClick={handleAddSection}
          className="w-fit rounded-xl border border-accent px-4 py-2 text-sm font-semibold text-accent hover:bg-accent-soft"
        >
          {autoReplyMessageStrings.addMessage}
        </button>
      )}
    </div>
  );
}
